// Copyright ©️ 2026 eVolve MEP, LLC

import { type Facility, type Project, useEvolvePost, type WorkOrderPaginated } from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';

import { FormModal } from 'components/Mantine/Modals/FormModal';
import type { RequireAtLeastOne } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { BendElementDetailsForm } from 'modules/Shop/EvolveFabrication/BendElementDetails/BendElementDetailsForm';

import { useBendCreatorCartContext } from '../BendCreator/FormComponents/Context/useBendCreatorCartContext';

type Props = {
  onBendsCreated: () => void;
  selectedFacilities?: Facility[];
} & RequireAtLeastOne<{
  workOrder?: WorkOrderPaginated;
  selectedProjects?: Project[];
}>;

const CreateBendElementDetailsForm = ({ onBendsCreated, ...props }: Props) => {
  const { apiCall: createBends } = useEvolvePost('shop/fabrication/bendCreator/batch');
  const { cart, onCartSubmitDone } = useBendCreatorCartContext();
  return (
    <BendElementDetailsForm
      onSubmit={async ({ workOrder, ...elementData }) => {
        if (isNil(workOrder)) return;
        await createBends({
          workOrderId: workOrder.workOrderId,
          bends: cart,
          ...elementData,
        });
        notifications.show({
          title: 'Successfully added bends',
          message: `Added ${cart.length} part${cart.length === 1 ? '' : 's'} to ${workOrder.name}.`,
          color: 'green',
        });
        onBendsCreated();
        onCartSubmitDone();
      }}
      {...props}
    />
  );
};

export const CreateBendElementDetailsModal = (props: Props) => {
  const { submitting, onCartSubmitDone } = useBendCreatorCartContext();
  return (
    <FormModal title="Add bends to work order" type="submit" opened={submitting} onClose={onCartSubmitDone} startDirty>
      <CreateBendElementDetailsForm {...props} />
    </FormModal>
  );
};
